import { Immutable } from "immer";
import React, { type JSX, useState } from "react";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import * as userAPI from "../../../api/user";
import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { FORM_CLASS_NAME } from "../../../forms/classNames";
import { useMountEffect } from "../../../hooks/useMountEffect";
import { strings } from "../../../localization";
import { CompleteOrderPaymentInitData } from "../../../models/checkout";
import { IUser } from "../../../models/user";
import { check } from "../../../models/utils";
import { setUser } from "../../../store/common/reducers";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import {
    filterFinancingPlans,
    sortFinancingPlans,
} from "../../../utils/financing";
import { objectKeys } from "../../../utils/functional";
import { BillingAddressSummary } from "../BillingAddressSummary";
import { CSRHeader } from "../CSRHeader";
import { CheckoutStep } from "../CheckoutStepContainer";
import { EmailAddressSummary } from "../EmailAddressSummary";
import { OrderSummary } from "../OrderSummary";
import { PaymentDeclineTracker } from "../PaymentDeclineTracker";
import { PaymentMethods } from "../PaymentMethods";
import { ShippingAddressSummary } from "../ShippingAddressSummary";
import { Step } from "../constants";
import {
    beginCompleteDeferredPayment,
    setFields,
    updateFinancingPlans,
    updatePaymentError,
} from "../reducers";
import {
    completeDeferredPayment as completeDeferredPaymentThunk,
    loadAssistedUser,
    loadCountries,
    loadEmailAddress,
    loadPaymentMethods,
    loadUserAddresses,
} from "../thunks";
import { ICheckoutHTTPError } from "../types";
import { IErrorMsgTemplateContext, renderErrorMessageTemplate } from "../utils";

interface IStepErrors {
    global?: string[];
    payment_method?: string[];
}

const parseInitData = (initDataJSON: string) => {
    return check(CompleteOrderPaymentInitData.decode(JSON.parse(initDataJSON)));
};

const renderStepLogin = (user: IUser | null): JSX.Element | null => {
    // Only display the email step if the user already has an email address. Otherwise,
    // it becomes part of the shipping address step.
    if (user && user.email && !user.is_csr) {
        return (
            <CheckoutStep
                key={Step.LOGIN}
                className="checkout-step--email checkout-step--first"
                heading={t`Email Address`}
            >
                <EmailAddressSummary user={user} />
            </CheckoutStep>
        );
    }
    return null;
};

const renderStepShippingAddress = (idx: number): JSX.Element => {
    return (
        <CheckoutStep
            key={Step.SHIPPING_ADDRESS}
            className={concatClassNames([
                "checkout-step--shipping-address",
                FORM_CLASS_NAME,
            ])}
            heading={`${idx}. ${strings.get(
                "CHECKOUT_SECTION_TITLE_SHIPPING_ADDRESS",
            )}`}
        >
            <ShippingAddressSummary />
        </CheckoutStep>
    );
};

const renderStepBillingAddress = (idx: number): JSX.Element => {
    return (
        <CheckoutStep
            key={Step.BILLING_ADDRESS}
            className={concatClassNames([
                "checkout-step--billing-address",
                FORM_CLASS_NAME,
            ])}
            heading={`${idx}. ${strings.get(
                "CHECKOUT_SECTION_TITLE_BILLING_ADDRESS",
            )}`}
        >
            <BillingAddressSummary />
        </CheckoutStep>
    );
};

const renderStepPaymentMethod = (
    idx: number,
    user: IUser | null,
    messages: IStepErrors,
    buildFinancingUpsell: (grandTotal: string) => JSX.Element | null,
    enableSplitPay: boolean | undefined,
    onPlaceOrder: () => void,
): JSX.Element => {
    const isCSR = user && user.is_csr;
    return (
        <PaymentMethods
            key={Step.PAYMENT_METHODS}
            heading={`${idx}. ${strings.get(
                "CHECKOUT_SECTION_TITLE_PAYMENT_METHODS",
            )}`}
            errors={messages.payment_method || []}
            buildFinancingUpsell={buildFinancingUpsell}
            enableSplitPay={enableSplitPay ? true : false}
            isCSR={!!isCSR}
            onContinue={onPlaceOrder}
        />
    );
};

const renderStep = (
    idx: number,
    step: Step,
    user: IUser | null,
    messages: IStepErrors,
    buildFinancingUpsell: (grandTotal: string) => JSX.Element | null,
    enableSplitPay: boolean | undefined,
    onPlaceOrder: () => void,
): JSX.Element | null => {
    switch (step) {
        case Step.LOGIN:
            return renderStepLogin(user);
        case Step.SHIPPING_ADDRESS:
            return renderStepShippingAddress(idx);
        case Step.BILLING_ADDRESS:
            return renderStepBillingAddress(idx);
        case Step.PAYMENT_METHODS:
            return renderStepPaymentMethod(
                idx,
                user,
                messages,
                buildFinancingUpsell,
                enableSplitPay,
                onPlaceOrder,
            );
        case Step.PLACING_ORDER:
        default:
            return null;
    }
};

export const CompleteDeferredPayment = (
    props: Immutable<{
        initDataJSON: string;
        buildFinancingUpsell: (grandTotal: string) => JSX.Element | null;
        enableSplitPay?: boolean;
        disableFreeShippingMessage?: boolean;
    }>,
): React.ReactNode => {
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [messages, setMessages] = useState<IStepErrors>({});

    const user = useAppSelector((rootState) => rootState.common.user);

    const initData = parseInitData(props.initDataJSON);

    useMountEffect(() => {
        const loadData = async () => {
            // Load all the data necessary to begin checkout
            const [loadedUser] = await Promise.all([
                userAPI.load().then((u) => {
                    dispatch(setUser(u));
                    return u;
                }),
                dispatch(loadCountries()),
                dispatch(loadPaymentMethods()),
            ]);

            // Load assisted user if the active user is a CSR
            if (loadedUser.is_csr) {
                try {
                    await dispatch(loadAssistedUser()).unwrap();
                } catch (e) {
                    console.warn(e);
                }
            }

            // Load the email address associated with the basket. Has to be deliberately
            // after the call to loadAssistedUser to prevent a race-condition.
            await Promise.all([
                dispatch(loadEmailAddress()),
                dispatch(loadUserAddresses()),
            ]);

            const sortedPlans = sortFinancingPlans(
                filterFinancingPlans(initData.financing_plans),
            );
            dispatch(updateFinancingPlans(sortedPlans));

            // Import shipping address
            const shippingFields: Record<string, unknown> = {};
            objectKeys(initData.shipping_address).forEach((key) => {
                const fieldKey = `shipping_${key}`;
                shippingFields[fieldKey] = initData.shipping_address[key];
            });
            dispatch(setFields(shippingFields));

            // Import billing address
            const billingFields: Record<string, unknown> = {};
            objectKeys(initData.billing_address).forEach((key) => {
                const fieldKey = `billing_${key}`;
                billingFields[fieldKey] = initData.billing_address[key];
            });
            dispatch(setFields(billingFields));

            // Load the order data into Redux
            dispatch(beginCompleteDeferredPayment({ order: initData.order }));
            // Jump to the payment step
            dispatch(
                setFields({
                    current_step: Step.PAYMENT_METHODS,
                }),
            );

            // Loading is complete
            setIsLoading(false);
        };

        void loadData();
    });

    const onCheckoutError = (err: ICheckoutHTTPError) => {
        console.error(err);

        const errors = (err && err.response ? err.response.body : {}) || {};
        const errorMessages: IStepErrors = {};
        const ctx: IErrorMsgTemplateContext = {
            orderID: initData.order.number,
        };
        if (errors.global) {
            dispatch(updatePaymentError(errors.global[0]));
        } else if (errors.payment) {
            errorMessages.payment_method = [
                renderErrorMessageTemplate(
                    strings.get("CHECKOUT_ERROR_PAYMENT_METHOD") || "",
                    ctx,
                ),
            ].concat(errors.payment);
        } else {
            errorMessages.payment_method = [
                renderErrorMessageTemplate(
                    strings.get("CHECKOUT_ERROR_FALLBACK") || "",
                    ctx,
                ),
            ];
        }
        setMessages(errorMessages);
    };

    const onPlaceOrder = () => {
        dispatch(
            completeDeferredPaymentThunk({
                basketToken: initData.basket.token,
                orderToken: initData.order.token,
            }),
        )
            .unwrap()
            .catch((err) => {
                onCheckoutError(err);
            });
    };

    if (isLoading) {
        return (
            <div className="checkout__react-container">
                <LoadingSpinner />
            </div>
        );
    }

    const formSteps = [
        Step.LOGIN,
        Step.SHIPPING_ADDRESS,
        Step.BILLING_ADDRESS,
        Step.PAYMENT_METHODS,
        Step.PLACING_ORDER,
    ];

    return (
        <div className="checkout__react-container">
            <div className="checkout__column--left">
                <CSRHeader user={user} />
                {formSteps.map((step, idx) =>
                    renderStep(
                        idx,
                        step,
                        user,
                        messages,
                        props.buildFinancingUpsell,
                        props.enableSplitPay,
                        onPlaceOrder,
                    ),
                )}
            </div>
            <div className="checkout__column--right basket-summary">
                <h2 className="basket-summary__header">{t`Order Summary`}</h2>
                <OrderSummary
                    order={initData.order}
                    disableFreeShippingMessage={
                        props.disableFreeShippingMessage
                    }
                />
            </div>
            <PaymentDeclineTracker />
        </div>
    );
};
